import React, { useCallback, useState } from "react";
import { PageSeo } from "@/components/seo/PageSeo";
import { useT } from "@/hooks/useT";
import { Hero } from "@/components/verified-documents/Hero";
import { ThreeStepStrip } from "@/components/verified-documents/ThreeStepStrip";
import { DocumentList } from "@/components/verified-documents/DocumentList";
import { Verifier } from "@/components/verified-documents/Verifier";
import { TechnicalDetails } from "@/components/verified-documents/TechnicalDetails";
import { Faq } from "@/components/verified-documents/Faq";

/**
 * /verified-documents (EN) and /zh/verified-documents (ZH).
 *
 * - Linked from Company mega-menu (header) and Resources (footer),
 *   both EN and ZH, from 2 Nov 2026 (operator release).
 * - Indexable; included in /sitemap.xml. The hero banner retains the
 *   [LEGAL-REVIEW] callout until the operator removes it separately.
 * - VERIFIED_DOCS_ENABLED runtime flag still governs the API/UI gate;
 *   when the flag is off the route resolves to NotFound via
 *   VerifiedDocsGate.
 * - Backend data fetched client-side from GET /api/documents.
 * - Hashing is purely in-browser via Web Crypto API; no file ever leaves
 *   the client.
 *
 * The DocumentList hoists its fetched array up here via `onLoad` so the
 * Verifier can match against the same list without a second request.
 */
export default function VerifiedDocuments() {
  const { t } = useT("verifiedDocuments");
  const [documents, setDocuments] = useState([]);

  const handleLoad = useCallback((docs) => {
    setDocuments(Array.isArray(docs) ? docs : []);
  }, []);

  return (
    <>
      <PageSeo
        title={t("seo.title")}
        description={t("seo.description")}
        path="/verified-documents"
        disableAlternates
      />

      <Hero />
      <ThreeStepStrip />
      <DocumentList onLoad={handleLoad} />
      <Verifier documents={documents} />
      <TechnicalDetails />
      <Faq />
    </>
  );
}
